import { syncTelegramChrome } from './theme';

const root = document.documentElement;

const px = (value: number | undefined): string => `${Math.max(0, value ?? 0)}px`;

const setInsetVariables = (prefix: string, inset: TelegramInset | undefined): void => {
  root.style.setProperty(`--${prefix}-top`, px(inset?.top));
  root.style.setProperty(`--${prefix}-right`, px(inset?.right));
  root.style.setProperty(`--${prefix}-bottom`, px(inset?.bottom));
  root.style.setProperty(`--${prefix}-left`, px(inset?.left));
};

const applyTelegramEnvironment = (webApp: TelegramWebApp): void => {
  root.dataset.telegramTheme = webApp.colorScheme;

  const theme = webApp.themeParams;
  const telegramVariables: Array<[string, string | undefined]> = [
    ['--tg-bg', theme.bg_color],
    ['--tg-text', theme.text_color],
    ['--tg-hint', theme.hint_color],
    ['--tg-secondary-bg', theme.secondary_bg_color],
    ['--tg-section-bg', theme.section_bg_color],
    ['--tg-accent', theme.accent_text_color]
  ];

  for (const [name, value] of telegramVariables) {
    if (value) {
      root.style.setProperty(name, value);
    } else {
      root.style.removeProperty(name);
    }
  }

  setInsetVariables('tg-safe', webApp.safeAreaInset);
  setInsetVariables('tg-content-safe', webApp.contentSafeAreaInset);

  // Telegram's chrome follows the UTAGE theme (light by default), not Telegram's own scheme.
  syncTelegramChrome();
};

export interface TelegramBridge {
  webApp: TelegramWebApp;
  initData: string;
  dispose(): void;
}

export const telegramStartParam = (webApp: TelegramWebApp): string | null =>
  webApp.initDataUnsafe?.start_param
  ?? new URLSearchParams(window.location.search).get('tgWebAppStartParam')
  ?? new URLSearchParams(window.location.search).get('startapp');

export const connectTelegram = (): TelegramBridge | null => {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) {
    return null;
  }

  applyTelegramEnvironment(webApp);

  const sync = (): void => applyTelegramEnvironment(webApp);
  webApp.onEvent('themeChanged', sync);
  webApp.onEvent('safeAreaChanged', sync);
  webApp.onEvent('contentSafeAreaChanged', sync);
  webApp.onEvent('activated', sync);
  webApp.onEvent('fullscreenChanged', sync);

  webApp.expand();
  webApp.ready();

  return {
    webApp,
    initData: webApp.initData,
    dispose: () => {
      webApp.offEvent('themeChanged', sync);
      webApp.offEvent('safeAreaChanged', sync);
      webApp.offEvent('contentSafeAreaChanged', sync);
      webApp.offEvent('activated', sync);
      webApp.offEvent('fullscreenChanged', sync);
    }
  };
};

// While a game is on screen, a downward swipe must reach the game (pull back in Mini Putt,
// steer in Snake) instead of pulling the whole mini app closed. Counted, so overlapping
// game screens only restore Telegram's swipe once the last one closes.
let swipeLocks = 0;
let swipesWereEnabled = true;
export const lockTelegramSwipes = (): (() => void) => {
  const webApp = window.Telegram?.WebApp;
  if (!webApp?.disableVerticalSwipes) return () => undefined;
  if (swipeLocks === 0) {
    swipesWereEnabled = webApp.isVerticalSwipesEnabled !== false;
    try { webApp.disableVerticalSwipes(); } catch { /* older Telegram: nothing to change */ }
  }
  swipeLocks += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    swipeLocks = Math.max(0, swipeLocks - 1);
    if (swipeLocks === 0 && swipesWereEnabled) {
      try { webApp.enableVerticalSwipes?.(); } catch { /* ignore */ }
    }
  };
};
